import { keyframes, style, styleVariants } from "@vanilla-extract/css";
import { primitiveTokens, semanticThemeTokens } from "../theme/ThemeTokens.css";

const Z = {
  overlay: 1000,
  content: 1001
} as const;

const fadeIn = keyframes({
  from: { opacity: 0 },
  to: { opacity: 1 }
});

export const overlay = style({
  position: "fixed",
  inset: 0,
  background: semanticThemeTokens.dialog.overlay.backgroundColor,
  animation: `${fadeIn} 150ms ease-out`,
  zIndex: Z.overlay
});

export const modalOverlay = style({
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  overflow: "auto",
  zIndex: Z.content
});
export const baseContent = style({
  background: semanticThemeTokens.dialog.content.background,
  color: semanticThemeTokens.dialog.content.textColor,
  borderRadius: primitiveTokens.radii.sm,
  boxShadow: semanticThemeTokens.dialog.content.boxShadow
});

export const content = styleVariants({
  sm: [
    {
      width: 420,
      padding: `${primitiveTokens.sizing["2x"]} ${primitiveTokens.sizing["4x"]}`
    }
  ],
  md: [
    {
      width: 560,
      padding: `${primitiveTokens.sizing["3x"]} ${primitiveTokens.sizing["5x"]}`
    }
  ],
  lg: [
    {
      width: 720,
      padding: `${primitiveTokens.sizing["4x"]} ${primitiveTokens.sizing["6x"]}`
    }
  ],
  xl: [
    {
      width: 1140,
      // Wide enough to meet windows narrower than itself; shrink, never clip.
      maxWidth: `calc(100vw - ${primitiveTokens.sizing["8x"]})`,
      padding: `${primitiveTokens.sizing["5x"]} ${primitiveTokens.sizing["7x"]}`
    }
  ],
  auto: [
    {
      padding: `${primitiveTokens.sizing["4x"]} ${primitiveTokens.sizing["6x"]}`
    }
  ],
  full: [
    {
      position: "fixed",
      inset: 0,
      transform: "none",
      width: "100dvw",
      height: "100dvh",
      maxWidth: "none",
      maxHeight: "none",
      borderRadius: 0,
      boxShadow: "none",
      boxSizing: "border-box",
      padding: `${primitiveTokens.sizing["3x"]} ${primitiveTokens.sizing["6x"]}`,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden"
    }
  ]
});
// The full size's title row is already as tall as its 40px icon buttons, so
// the usual header-to-body spacing would double the gap above the content.
export const header = style({
  paddingBottom: 8,
  selectors: { [`${content.full} &`]: { paddingBottom: 0 } }
});

export const title = style({
  flex: "1 1 auto",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  fontSize: semanticThemeTokens.dialog.title.fontSize
});

export const description = style({
  marginTop: primitiveTokens.sizing["2x"],
  color: semanticThemeTokens.dialog.description.textColor,
  fontSize: semanticThemeTokens.dialog.description.fontSize
});

// `minHeight: 0` lets the full size's body shrink to the space left, so
// content that scrolls itself keeps what sits below it on screen. No
// `overflow` here: it would clip the item modal's collapse animation.
export const body = style({
  marginTop: primitiveTokens.sizing["3x"],
  flex: "1 1 auto",
  minHeight: 0,
  selectors: {
    [`${content.full} &`]: { marginTop: primitiveTokens.sizing["2x"] }
  }
});

// Transparent to the layout: keeps the content's flex column running
// through body and footer, so the full size's body can shrink (content
// inside it scrolls itself).
export const form = style({
  display: "flex",
  flexDirection: "column",
  flex: "1 1 auto",
  minHeight: 0
});

export const footer = style({
  display: "flex",
  justifyContent: "flex-end",
  gap: primitiveTokens.sizing["2x"],
  marginTop: primitiveTokens.sizing["5x"]
});

export const iconButton = style({
  position: "absolute",
  top: primitiveTokens.sizing["3x"],
  right: primitiveTokens.sizing["3x"],
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center"
});

export const titleRow = style({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: primitiveTokens.sizing["3x"],
  width: "100%",
  flexWrap: "nowrap"
});

export const titleActions = style({
  display: "flex",
  alignItems: "center",
  gap: primitiveTokens.sizing["1x"],
  flex: "0 0 auto"
});
